/* The offline pack (02 §3.7, 09-brief M4).
 *
 * Pyodide is ~13 MB, so downloading it is an explicit decision in Settings with
 * a Wi-Fi recommendation — never something the app does behind Jochem's back on
 * a cellular connection.
 *
 * The pack lives in the same Cache Storage bucket the service worker's
 * CacheFirst route reads, so "installed" is not a flag we set: it is the literal
 * presence of every manifest file in that cache. A half-finished download can
 * therefore never report itself as installed.
 */

import { PYODIDE_BASE, PYODIDE_CACHE_NAME, PYODIDE_PACK_VERSION } from '@ascent/shared';

export interface PackManifest {
  kind: string;
  version: string;
  base: string;
  note: string;
  total_bytes: number;
  files: { name: string; bytes: number }[];
}

export interface PackState {
  version: string;
  installed: boolean;
  /** Bytes actually present in the cache — partial installs show their progress. */
  bytesPresent: number;
  totalBytes: number;
  /** Null when the manifest itself could not be read (offline, never installed). */
  manifest: PackManifest | null;
}

let manifestCache: PackManifest | null = null;

/** The manifest is generated at build time by scripts/vendor-runtime.mjs. */
export async function loadManifest(): Promise<PackManifest | null> {
  if (manifestCache) return manifestCache;
  try {
    const res = await fetch(`${PYODIDE_BASE}pack-manifest.json`, { cache: 'no-store' });
    if (!res.ok) return null;
    manifestCache = (await res.json()) as PackManifest;
    return manifestCache;
  } catch {
    return null;
  }
}

function urlsOf(manifest: PackManifest): string[] {
  return manifest.files.map((f) => `${manifest.base}${f.name}`);
}

export async function packState(): Promise<PackState> {
  const manifest = await loadManifest();
  if (!('caches' in globalThis)) {
    return { version: PYODIDE_PACK_VERSION, installed: false, bytesPresent: 0, totalBytes: manifest?.total_bytes ?? 0, manifest };
  }

  const cache = await caches.open(PYODIDE_CACHE_NAME);

  // Without a manifest we can still answer "is something cached", which is what
  // decides whether an offline run is even worth attempting.
  if (!manifest) {
    const keys = await cache.keys();
    return {
      version: PYODIDE_PACK_VERSION,
      installed: keys.length > 0,
      bytesPresent: 0,
      totalBytes: 0,
      manifest: null,
    };
  }

  let bytesPresent = 0;
  let missing = 0;
  for (const file of manifest.files) {
    const hit = await cache.match(`${manifest.base}${file.name}`, { ignoreVary: true });
    if (hit) bytesPresent += file.bytes;
    else missing++;
  }

  return {
    version: manifest.version,
    installed: missing === 0,
    bytesPresent,
    totalBytes: manifest.total_bytes,
    manifest,
  };
}

/**
 * Download the pack, reporting progress per file.
 *
 * Files are added one at a time rather than through `cache.addAll`, because
 * addAll is all-or-nothing with no progress: on a phone, 13 MB of silence is
 * indistinguishable from a hang.
 */
export async function installPack(
  onProgress: (bytesDone: number, totalBytes: number) => void,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const manifest = await loadManifest();
  if (!manifest) return { ok: false, error: 'The pack list could not be read. Connect once and try again.' };

  const cache = await caches.open(PYODIDE_CACHE_NAME);
  let done = 0;

  for (const file of manifest.files) {
    const url = `${manifest.base}${file.name}`;
    try {
      // Bypass the SW's CacheFirst for the download itself: a half-cached pack
      // must be repairable, and CacheFirst would just hand back the bad entry.
      const res = await fetch(url, { cache: 'reload' });
      if (!res.ok) return { ok: false, error: `${file.name} failed to download (${res.status}).` };
      await cache.put(url, res.clone());
    } catch {
      return { ok: false, error: 'The download stopped. Check your connection and try again — finished files are kept.' };
    }
    done += file.bytes;
    onProgress(done, manifest.total_bytes);
  }

  return { ok: true };
}

export async function removePack(): Promise<void> {
  await caches.delete(PYODIDE_CACHE_NAME);
}

/** Human-facing size. MB, one decimal — the honest number from the manifest. */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${bytes} B`;
}

/**
 * Can a `python` exercise run right now?
 *
 * Online, yes — the runtime lazy-loads over the network. Offline, only with the
 * pack installed. This is the exact condition behind the blocked state and the
 * substitution rule of 02 §6.10.
 */
export async function pythonRunnable(): Promise<boolean> {
  if (navigator.onLine) return true;
  const state = await packState();
  return state.installed;
}

export { PYODIDE_PACK_VERSION };
